import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon, CloseIcon } from '../../../../bombiix-icon';

export interface NewSearchProps {
  value: string;
  onChange: (_value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  width?: number;
  testId?: string;
  inputId?: string;
  autoFocus?: boolean;
  showClear?: boolean;
  showIcon?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  onEnter?: () => void;
  maxLength?: number;
}

/**
 * NewSearch — shared search input. Visual styling comes from the global
 * `.bombiix-search` class (master-design `_search.scss`), passed via
 * `className`. `disabled` adds `is-disabled` and disables the field; `invalid`
 * adds `is-invalid` (required-validation red border) and marks the input
 * aria-invalid. `showIcon={false}` hides the trailing glyph (for a field paired
 * with an external search button); `onEnter` fires on the Enter key.
 * `maxLength` caps the term the caller can type or paste. See `/master-design`
 * for the idle / disabled / required states.
 */
export function NewSearch({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
  width,
  testId,
  inputId,
  autoFocus = false,
  showClear = true,
  showIcon = true,
  disabled = false,
  invalid = false,
  onEnter,
  maxLength,
}: NewSearchProps): ReactElement {
  const { t: tRaw } = useTranslation('pim');
  const t = (key: string): string => String(tRaw(key));
  const rootClass = [className, invalid ? 'is-invalid' : '', disabled ? 'is-disabled' : ''].filter(Boolean).join(' ');
  const style = width ? { width: `${width}px` } : undefined;
  return (
    <div className={rootClass} style={style}>
      <input
        type="text"
        id={inputId}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder ?? t('PIM.COMMON.SEARCH')}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        maxLength={maxLength}
        onChange={(e) => onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
        onKeyDown={
          onEnter
            ? (e) => {
                if (e.key === 'Enter') onEnter();
              }
            : undefined
        }
        data-testid={testId}
        autoFocus={autoFocus}
      />
      {showIcon ? <SearchIcon size={24} data-testid="new-search-icon" aria-hidden="true" /> : null}
      {showClear && !disabled && value.length > 0 ? (
        <button type="button" aria-label={t('PIM.COMMON.CLEAR_SEARCH')} onClick={() => onChange('')}>
          <CloseIcon size={24} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
